/**
 * Importación múltiple de facturas de proveedores a "Por confirmar".
 *
 * El proveedor manda una carpeta con decenas de facturas; cada una son 3 archivos: el XML de la
 * factura, el XML de la respuesta de Hacienda y el PDF. Esta ventana:
 *   1. recibe la carpeta (arrastrada, elegida o en .zip),
 *   2. el servidor agrupa cada factura con sus 3 archivos POR CONTENIDO y valida las reglas
 *      (empresa activa, aceptada por Hacienda, respuesta que cuadra, PDF con certeza, no repetida),
 *   3. importa las listas, una por una, viendo el avance,
 *   4. y al final permite confirmarlas todas ante Hacienda sin salir (misma confirmación masiva
 *      de la pantalla, la recibe por `ConfirmarTodas`).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  X, UploadCloud, FolderOpen, FileArchive, FileText, FileCode2, ShieldCheck, CheckCircle2, XCircle,
  AlertTriangle, Loader2, Building2, Paperclip, ChevronDown, ChevronRight, ChevronLeft, Inbox, Copy, Ban, Scale, Eye
} from 'lucide-react';
import toast from 'react-hot-toast';
import { gastosApi } from '../../utils/api';
import ConfirmModal from '../ConfirmModal';

const LIMITE_ENVIO = 45 * 1024 * 1024; // el servidor web acepta 50 MB por envío

// Cada estado del análisis, explicado en palabras simples
const ESTADOS = {
  lista:        { titulo: 'Listas para importar', corto: 'Lista', color: 'emerald', icono: CheckCircle2 },
  incompleta:   { titulo: 'Les falta un archivo', corto: 'Incompleta', color: 'amber', icono: Paperclip },
  no_aceptada:  { titulo: 'No aceptadas por Hacienda', corto: 'No aceptada', color: 'red', icono: XCircle },
  otra_empresa: { titulo: 'De otra empresa', corto: 'Otra empresa', color: 'violet', icono: Building2 },
  duplicada:    { titulo: 'Ya registradas', corto: 'Ya registrada', color: 'slate', icono: Copy },
  no_cuadra:    { titulo: 'Respuesta no cuadra', corto: 'No cuadra', color: 'red', icono: Scale },
  conflicto:    { titulo: 'Archivos contradictorios', corto: 'Conflicto', color: 'red', icono: AlertTriangle },
  no_aplica:    { titulo: 'No son gastos', corto: 'No aplica', color: 'slate', icono: Ban }
};
const ORDEN = ['lista', 'incompleta', 'no_aceptada', 'no_cuadra', 'conflicto', 'otra_empresa', 'duplicada', 'no_aplica'];

const TONO = {
  emerald: { borde: 'border-emerald-200', fondo: 'bg-emerald-50', texto: 'text-emerald-700', fuerte: 'text-emerald-900', anillo: 'ring-emerald-400' },
  amber:   { borde: 'border-amber-200', fondo: 'bg-amber-50', texto: 'text-amber-700', fuerte: 'text-amber-900', anillo: 'ring-amber-400' },
  red:     { borde: 'border-red-200', fondo: 'bg-red-50', texto: 'text-red-700', fuerte: 'text-red-900', anillo: 'ring-red-400' },
  violet:  { borde: 'border-violet-200', fondo: 'bg-violet-50', texto: 'text-violet-700', fuerte: 'text-violet-900', anillo: 'ring-violet-400' },
  slate:   { borde: 'border-slate-200', fondo: 'bg-slate-50', texto: 'text-slate-600', fuerte: 'text-slate-900', anillo: 'ring-slate-400' }
};

const monto = (n, moneda) => new Intl.NumberFormat('es-CR', { style: 'currency', currency: moneda === 'USD' ? 'USD' : 'CRC', minimumFractionDigits: 2 }).format(Number(n) || 0);
const corto = (c) => (c ? String(Number(String(c).slice(-10))) : '—');
const fecha = (f) => (f ? new Date(f).toLocaleDateString('es-CR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
// Condición de venta según el XML: 01 contado; el resto se acepta a crédito (igual que la confirmación masiva)
const esContado = (cv) => String(cv || '') === '01';
const textoCondicion = (cv, plazo) => {
  if (!cv) return '—';
  if (esContado(cv)) return 'Contado';
  if (String(cv) === '02') return Number(plazo) > 0 ? `Crédito ${Number(plazo)} días` : 'Crédito';
  return `Crédito (cond. ${cv})`;
};
function Condicion({ cv, plazo }) {
  if (!cv) return <span className="text-gray-300">—</span>;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-semibold whitespace-nowrap ${esContado(cv) ? 'border-sky-300 text-sky-700 bg-sky-50' : 'border-amber-300 text-amber-800 bg-amber-50'}`}>
      {textoCondicion(cv, plazo)}
    </span>
  );
}
const pesoTexto = (b) => (!b ? '0 KB' : b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

/** Recorre una carpeta arrastrada (con subcarpetas) y devuelve sus archivos */
async function archivosDeArrastre(dataTransfer) {
  const entradas = [...(dataTransfer.items || [])].map(i => i.webkitGetAsEntry?.()).filter(Boolean);
  if (!entradas.length) return [...(dataTransfer.files || [])];
  const salida = [];
  const leer = (entrada) => new Promise((resolve) => {
    if (entrada.isFile) { entrada.file(f => { salida.push(f); resolve(); }, () => resolve()); return; }
    const lector = entrada.createReader();
    const todo = [];
    const tanda = () => lector.readEntries(async (lote) => {
      if (!lote.length) { await Promise.all(todo.map(leer)); resolve(); return; }
      todo.push(...lote); tanda();
    }, () => resolve());
    tanda();
  });
  await Promise.all(entradas.map(leer));
  return salida;
}

function Archivo({ ok, icono: Icono, etiqueta, nombre, detalle, onVer }) {
  const clase = `inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-medium whitespace-nowrap ${ok ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-dashed border-red-300 bg-red-50/60 text-red-600'}`;
  if (ok && onVer) {
    return (
      <button type="button" onClick={onVer} title={`Ver ${nombre}`} className={`${clase} hover:bg-emerald-100 hover:border-emerald-400 transition`}>
        <Icono className="w-3.5 h-3.5" /> {etiqueta} <Eye className="w-3.5 h-3.5 opacity-70" />
      </button>
    );
  }
  return (
    <span title={nombre || `Falta ${etiqueta}`} className={clase}>
      <Icono className="w-3.5 h-3.5" /> {etiqueta}{detalle ? ` · ${detalle}` : ''}
    </span>
  );
}

/**
 * Vista previa del PDF junto a los datos del XML, para validar rápido que corresponden.
 * Las flechas (o ← →) pasan a la factura anterior o siguiente sin cerrar.
 */
function VisorPdf({ loteId, lista, indice, setIndice, onCerrar }) {
  const f = lista[indice];
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let vivo = true; let creada = null;
    setUrl(null); setError(null);
    gastosApi.loteArchivo(loteId, f.archivos.pdfInterno)
      .then(r => { if (!vivo) return; creada = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })); setUrl(creada); })
      .catch(() => vivo && setError('No se pudo abrir el PDF'));
    return () => { vivo = false; if (creada) URL.revokeObjectURL(creada); };
  }, [loteId, f.archivos.pdfInterno]);
  useEffect(() => {
    const tecla = (e) => {
      if (e.key === 'Escape') onCerrar();
      if (e.key === 'ArrowRight' && indice < lista.length - 1) setIndice(indice + 1);
      if (e.key === 'ArrowLeft' && indice > 0) setIndice(indice - 1);
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [indice, lista.length, onCerrar, setIndice]);

  const dato = (t, v, fuerte) => (
    <div className="py-2.5 border-b border-gray-100 last:border-0">
      <p className="text-[11px] uppercase tracking-wider text-gray-400">{t}</p>
      <p className={`mt-0.5 ${fuerte ? 'text-lg font-bold text-gray-900 tabular-nums' : 'text-sm text-gray-800'}`}>{v || '—'}</p>
    </div>
  );
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onCerrar}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl h-[92vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-gray-200">
          <div className="flex items-center gap-3 min-w-0">
            <FileText className="w-5 h-5 text-indigo-600 shrink-0" />
            <p className="text-sm font-semibold text-gray-900 truncate">{f.archivos.pdf}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 tabular-nums whitespace-nowrap">{indice + 1} de {lista.length}</span>
            <button onClick={() => setIndice(indice - 1)} disabled={indice === 0} className="p-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-30" title="Anterior (←)"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => setIndice(indice + 1)} disabled={indice === lista.length - 1} className="p-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-30" title="Siguiente (→)"><ChevronRight className="w-4 h-4" /></button>
            <button onClick={onCerrar} className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100" title="Cerrar (Esc)"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="flex-1 grid lg:grid-cols-[1fr_320px] min-h-0">
          <div className="bg-gray-100 min-h-0">
            {url ? <iframe title="PDF" src={url} className="w-full h-full border-0" />
              : error ? <div className="h-full flex items-center justify-center text-sm text-red-600">{error}</div>
                : <div className="h-full flex items-center justify-center"><Loader2 className="w-8 h-8 text-indigo-600 animate-spin" /></div>}
          </div>
          <div className="border-l border-gray-200 p-5 overflow-y-auto">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">Según el XML</p>
            <p className="text-[11px] text-gray-400 mb-3">Compará estos datos con el PDF</p>
            {dato('Factura', `${f.tipo || ''} ${corto(f.consecutivo)}`)}
            {dato('Consecutivo completo', f.consecutivo)}
            {dato('Proveedor', f.proveedor)}
            {dato('Cédula del proveedor', f.cedulaProveedor)}
            {dato('Fecha', fecha(f.fecha))}
            {dato('Condición', textoCondicion(f.condicionVenta, f.plazoCredito))}
            {dato('IVA', f.totalImpuesto != null ? monto(f.totalImpuesto, f.moneda) : null)}
            {dato('Total', f.total != null ? monto(f.total, f.moneda) : null, true)}
            {dato('Hacienda', f.archivos.respuestaEstado)}
            <p className="text-[11px] text-gray-400 mt-3">PDF emparejado por {f.archivos.pdfComo || '—'}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ImportacionMultipleModal({ empresa, onClose, onSuccess, ConfirmarTodas }) {
  const [fase, setFase] = useState('seleccion');        // seleccion | subiendo | resultado | importando | fin
  const [archivos, setArchivos] = useState([]);
  const [dejados, setDejados] = useState([]);          // archivos que no son XML, PDF ni ZIP
  const [arrastrando, setArrastrando] = useState(false);
  const [progreso, setProgreso] = useState(0);
  const [loteId, setLoteId] = useState(null);
  const [analisis, setAnalisis] = useState(null);
  const [filtro, setFiltro] = useState('lista');
  const [marcadas, setMarcadas] = useState(new Set());
  const [avance, setAvance] = useState({});             // clave → { estado: 'cola'|'subiendo'|'ok'|'error', mensaje, facturaId }
  const [verIgnorados, setVerIgnorados] = useState(false);
  const [confirmarImportar, setConfirmarImportar] = useState(false);
  const [confirmando, setConfirmando] = useState(false); // abre la confirmación masiva ante Hacienda
  const [visor, setVisor] = useState(null);             // { lista, indice } del PDF que se está viendo
  const inputArchivos = useRef(null);

  // ── selección ──
  const agregar = (lista) => {
    const utiles = lista.filter(f => /\.(xml|pdf|zip)$/i.test(f.name));
    const otros = lista.filter(f => !/\.(xml|pdf|zip)$/i.test(f.name) && !f.name.startsWith('.'));
    setArchivos(prev => {
      const ya = new Set(prev.map(f => `${f.name}|${f.size}`));
      return [...prev, ...utiles.filter(f => !ya.has(`${f.name}|${f.size}`))];
    });
    // Se muestran dentro de la tarjeta de resumen (sin avisos flotantes)
    setDejados(prev => [...new Set([...prev, ...otros.map(f => f.name)])]);
  };
  const resumenSeleccion = useMemo(() => {
    const c = { xml: 0, pdf: 0, zip: 0, peso: 0 };
    for (const f of archivos) { c.peso += f.size; if (/\.xml$/i.test(f.name)) c.xml++; else if (/\.pdf$/i.test(f.name)) c.pdf++; else c.zip++; }
    return c;
  }, [archivos]);

  // ── subir y analizar ──
  const analizar = async () => {
    if (!archivos.length) return;
    setFase('subiendo'); setProgreso(0);
    try {
      let id = loteId;
      if (!id) { id = (await gastosApi.loteCrear()).data.data.loteId; setLoteId(id); }
      // Un solo envío salvo que pase el límite del servidor web (entonces se parte solo)
      const tandas = []; let actual = []; let peso = 0;
      for (const f of archivos) {
        if (actual.length && (peso + f.size > LIMITE_ENVIO || actual.length >= 290)) { tandas.push(actual); actual = []; peso = 0; }
        actual.push(f); peso += f.size;
      }
      if (actual.length) tandas.push(actual);
      const total = archivos.reduce((s, f) => s + f.size, 0) || 1;
      let enviado = 0;
      for (const t of tandas) {
        const fd = new FormData();
        t.forEach(f => fd.append('archivos', f, f.name));
        const base = enviado;
        await gastosApi.loteSubir(id, fd, (e) => setProgreso(Math.min(99, Math.round(((base + (e.loaded || 0)) / total) * 100))));
        enviado += t.reduce((s, f) => s + f.size, 0);
      }
      setProgreso(100);
      const r = (await gastosApi.loteAnalizar(id)).data.data;
      setAnalisis(r);
      setArchivos([]);
      setDejados([]);
      setMarcadas(new Set(r.facturas.filter(f => f.estado === 'lista').map(f => f.clave)));
      setFiltro(r.conteo.lista ? 'lista' : (ORDEN.find(e => r.conteo[e]) || 'lista'));
      setFase('resultado');
    } catch (e) {
      toast.error(e.response?.data?.error || 'No se pudieron analizar los archivos');
      setFase(analisis ? 'resultado' : 'seleccion');
    }
  };

  const asignarPdf = async (pdf, clave) => {
    try {
      const r = (await gastosApi.loteAsignarPdf(loteId, pdf, clave)).data.data;
      setAnalisis(r);
      setMarcadas(prev => new Set([...prev, ...r.facturas.filter(f => f.estado === 'lista' && f.clave === clave).map(f => f.clave)]));
    } catch (e) { toast.error(e.response?.data?.error || 'No se pudo asignar el PDF'); }
  };

  // ── importar, una por una ──
  const listas = analisis?.facturas.filter(f => f.estado === 'lista') || [];
  const aImportar = listas.filter(f => marcadas.has(f.clave));
  const importar = async () => {
    setConfirmarImportar(false);
    setFase('importando');
    const inicial = {};
    aImportar.forEach(f => { inicial[f.clave] = { estado: 'cola' }; });
    setAvance(inicial);
    for (const f of aImportar) {
      setAvance(prev => ({ ...prev, [f.clave]: { estado: 'subiendo' } }));
      try {
        const r = (await gastosApi.loteImportar(loteId, f.clave)).data.data;
        setAvance(prev => ({ ...prev, [f.clave]: { estado: 'ok', facturaId: r.facturaId } }));
      } catch (e) {
        setAvance(prev => ({ ...prev, [f.clave]: { estado: 'error', mensaje: e.response?.data?.error || e.message } }));
      }
    }
    setFase('fin');
  };

  const importadas = aImportar.filter(f => avance[f.clave]?.estado === 'ok');
  const fallidas = aImportar.filter(f => avance[f.clave]?.estado === 'error');
  const hechas = aImportar.filter(f => ['ok', 'error'].includes(avance[f.clave]?.estado)).length;

  const totalesPorMoneda = (lista) => lista.reduce((m, f) => { m[f.moneda || 'CRC'] = (m[f.moneda || 'CRC'] || 0) + (Number(f.total) || 0); return m; }, {});

  const cerrar = () => {
    if (fase === 'subiendo' || fase === 'importando') return;
    if (loteId) gastosApi.loteBorrar(loteId).catch(() => {});
    if (importadas.length) onSuccess(); else onClose();
  };

  // ── piezas de la vista ──
  const encabezado = (
    <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-gray-200">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-200 flex items-center justify-center shrink-0">
          <FolderOpen className="w-5 h-5 text-indigo-600" />
        </div>
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 leading-tight">Importación múltiple de facturas</h3>
          <p className="text-xs text-gray-500">Cada factura entra con sus 3 archivos: XML, respuesta de Hacienda y PDF</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-indigo-200 bg-indigo-50 text-indigo-700 text-xs font-medium whitespace-nowrap">
          <Building2 className="w-3.5 h-3.5" /> {empresa?.nombre} {empresa?.identificacion ? `· ${empresa.identificacion}` : ''}
        </span>
        <button onClick={cerrar} disabled={fase === 'subiendo' || fase === 'importando'} className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30">
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );

  const pasos = (
    <div className="flex items-center gap-2 px-6 py-3 bg-gray-50 border-b border-gray-200 text-xs">
      {[['seleccion', '1', 'Subir la carpeta'], ['resultado', '2', 'Revisar el análisis'], ['importando', '3', 'Importar'], ['fin', '4', 'Confirmar ante Hacienda']].map(([clave, n, texto], i, arr) => {
        const orden = ['seleccion', 'subiendo', 'resultado', 'importando', 'fin'];
        const activo = orden.indexOf(fase) >= orden.indexOf(clave);
        return (
          <div key={clave} className="flex items-center gap-2">
            <span className={`w-5 h-5 rounded-full flex items-center justify-center font-semibold ${activo ? 'bg-indigo-600 text-white' : 'bg-white border border-gray-300 text-gray-400'}`}>{n}</span>
            <span className={activo ? 'text-gray-900 font-medium' : 'text-gray-400'}>{texto}</span>
            {i < arr.length - 1 && <ChevronRight className="w-3.5 h-3.5 text-gray-300" />}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[92vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        {encabezado}
        {pasos}

        <div className="flex-1 overflow-y-auto">
          {/* ───────── 1. Selección ───────── */}
          {(fase === 'seleccion' || fase === 'subiendo') && (
            <div className="grid lg:grid-cols-[1fr_380px] gap-6 p-6">
              <div
                onDragOver={e => { e.preventDefault(); setArrastrando(true); }}
                onDragLeave={() => setArrastrando(false)}
                onDrop={async e => { e.preventDefault(); setArrastrando(false); agregar(await archivosDeArrastre(e.dataTransfer)); }}
                className={`relative rounded-2xl border-2 border-dashed transition-all min-h-[340px] flex flex-col items-center justify-center text-center p-8 ${arrastrando ? 'border-indigo-500 bg-indigo-50/70 scale-[1.01]' : 'border-gray-300 bg-gradient-to-b from-white to-gray-50'}`}>
                {fase === 'subiendo' ? (
                  <div className="w-full max-w-md">
                    <Loader2 className="w-10 h-10 text-indigo-600 animate-spin mx-auto mb-4" />
                    <p className="text-base font-semibold text-gray-900 mb-1">{progreso < 100 ? 'Subiendo los archivos…' : 'Agrupando y validando cada factura…'}</p>
                    <p className="text-sm text-gray-500 mb-4">{progreso < 100 ? `${progreso}%` : 'Leyendo la clave de cada XML y emparejando sus archivos'}</p>
                    <div className="h-2 rounded-full bg-gray-200 overflow-hidden"><div className="h-full bg-indigo-600 transition-all" style={{ width: `${progreso}%` }} /></div>
                  </div>
                ) : (
                  <>
                    <div className="w-16 h-16 rounded-2xl bg-indigo-50 border border-indigo-200 flex items-center justify-center mb-4">
                      <UploadCloud className="w-8 h-8 text-indigo-600" />
                    </div>
                    <p className="text-lg font-semibold text-gray-900">Arrastrá aquí la carpeta del proveedor</p>
                    <p className="text-sm text-gray-500 mt-1 mb-5">También se acepta un .zip con todo adentro.</p>
                    {/* Sin botón de "elegir carpeta": ese camino hace que Chrome muestre su propio aviso de
                        seguridad ("Upload N files to this site?"), que ninguna página puede reemplazar. */}
                    <button onClick={() => inputArchivos.current?.click()} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-indigo-300 text-indigo-700 hover:bg-indigo-50 text-sm font-medium">
                      <FileText className="w-4 h-4" /> Elegir archivos o ZIP
                    </button>
                    <p className="text-xs text-gray-400 mt-3">Para elegir toda la carpeta: entrá a ella y seleccioná todo con ⌘A (Ctrl+A en Windows).</p>
                    <input ref={inputArchivos} type="file" multiple accept=".xml,.pdf,.zip" className="hidden" onChange={e => { agregar([...(e.target.files || [])]); e.target.value = ''; }} />
                  </>
                )}
              </div>

              <div className="space-y-4">
                <div className="rounded-2xl border border-gray-200 p-5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Lo que se va a subir</p>
                  <div className="grid grid-cols-3 gap-3">
                    {[['XML', resumenSeleccion.xml, FileCode2], ['PDF', resumenSeleccion.pdf, FileText], ['ZIP', resumenSeleccion.zip, FileArchive]].map(([t, n, I]) => (
                      <div key={t} className="rounded-xl bg-gray-50 border border-gray-200 p-3 text-center">
                        <I className="w-4 h-4 text-gray-400 mx-auto mb-1" />
                        <p className="text-2xl font-bold text-gray-900 leading-none">{n}</p>
                        <p className="text-[11px] text-gray-500 mt-1">{t}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-3">{archivos.length} archivo(s) · {pesoTexto(resumenSeleccion.peso)}</p>
                  {dejados.length > 0 && (
                    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                      <p className="flex items-center gap-1.5 text-xs font-medium text-slate-700">
                        <Inbox className="w-3.5 h-3.5 text-slate-400" /> {dejados.length === 1 ? 'Se deja afuera 1 archivo' : `Se dejan afuera ${dejados.length} archivos`}
                      </p>
                      <ul className="mt-1 space-y-0.5">
                        {dejados.slice(0, 3).map(n => <li key={n} className="text-[11px] text-slate-500 truncate" title={n}>{n}</li>)}
                        {dejados.length > 3 && <li className="text-[11px] text-slate-400">y {dejados.length - 3} más</li>}
                      </ul>
                      <p className="text-[11px] text-slate-400 mt-1">Solo se usan XML, PDF y ZIP.</p>
                    </div>
                  )}
                  {archivos.length > 0 && fase === 'seleccion' && (
                    <button onClick={() => { setArchivos([]); setDejados([]); }} className="text-xs text-gray-500 hover:text-red-600 mt-2">Quitar todos</button>
                  )}
                </div>
                <div className="rounded-2xl border border-indigo-100 bg-indigo-50/50 p-5">
                  <p className="flex items-center gap-2 text-sm font-semibold text-indigo-900 mb-2"><ShieldCheck className="w-4 h-4" /> Qué se revisa en cada factura</p>
                  <ul className="text-xs text-indigo-900/80 space-y-1.5 list-disc pl-4">
                    <li>Que tenga sus 3 archivos, emparejados por la clave del XML, no por el nombre.</li>
                    <li>Que Hacienda la haya <b>aceptado</b> y que la respuesta cuadre con la factura.</li>
                    <li>Que sea de <b>{empresa?.nombre || 'la empresa activa'}</b>.</li>
                    <li>Que sea FE, NC o ND, y que no esté ya registrada.</li>
                  </ul>
                  <p className="text-[11px] text-indigo-900/60 mt-2">Solo entran las que pasan todo. El resto se muestra con el motivo.</p>
                </div>
              </div>
            </div>
          )}

          {/* ───────── 2. Resultado del análisis ───────── */}
          {fase === 'resultado' && analisis && (() => {
            const visibles = analisis.facturas.filter(f => f.estado === filtro);
            const totSel = totalesPorMoneda(aImportar);
            return (
              <div className="p-6 space-y-5">
                {/* Resumen grande */}
                <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
                  <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-5 sm:col-span-2 xl:col-span-1">
                    <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Listas para importar</p>
                    <p className="text-4xl font-bold text-emerald-900 mt-1">{analisis.conteo.lista || 0}<span className="text-lg text-emerald-700/70 font-semibold"> / {analisis.facturas.length}</span></p>
                    <p className="text-xs text-emerald-800/80 mt-1">facturas detectadas en {analisis.archivos} archivos</p>
                  </div>
                  <div className="rounded-2xl border border-gray-200 p-5">
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Monto seleccionado</p>
                    {Object.keys(totSel).length === 0 ? <p className="text-2xl font-bold text-gray-300 mt-1">—</p> :
                      Object.entries(totSel).map(([m, t]) => <p key={m} className="text-2xl font-bold text-gray-900 mt-1 tabular-nums">{monto(t, m)}</p>)}
                    <p className="text-xs text-gray-500 mt-1">{aImportar.length} factura(s) marcada(s)</p>
                    {aImportar.length > 0 && (() => {
                      const contado = aImportar.filter(f => esContado(f.condicionVenta)).length;
                      const credito = aImportar.length - contado;
                      return (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {credito > 0 && <span className="px-2 py-0.5 rounded-full border border-amber-300 bg-amber-50 text-amber-800 text-[11px] font-semibold">{credito} a crédito</span>}
                          {contado > 0 && <span className="px-2 py-0.5 rounded-full border border-sky-300 bg-sky-50 text-sky-700 text-[11px] font-semibold">{contado} de contado</span>}
                        </div>
                      );
                    })()}
                  </div>
                  <div className="rounded-2xl border border-gray-200 p-5">
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Archivos</p>
                    <p className="text-sm text-gray-800 mt-2 flex items-center gap-2"><Paperclip className="w-4 h-4 text-gray-400" /> {analisis.pdfSinAsignar.length} PDF sin asignar</p>
                    <p className="text-sm text-gray-800 mt-1 flex items-center gap-2"><Inbox className="w-4 h-4 text-gray-400" /> {analisis.ignorados.length} archivo(s) ignorado(s)</p>
                  </div>
                  <div className="rounded-2xl border border-gray-200 p-5">
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Con problemas</p>
                    <p className="text-4xl font-bold text-gray-900 mt-1">{analisis.facturas.length - (analisis.conteo.lista || 0)}</p>
                    <p className="text-xs text-gray-500 mt-1">no se importan; el motivo está en cada una</p>
                  </div>
                </div>

                {/* Filtro por estado */}
                <div className="flex flex-wrap gap-2">
                  {ORDEN.filter(e => analisis.conteo[e]).map(e => {
                    const d = ESTADOS[e]; const t = TONO[d.color]; const I = d.icono;
                    return (
                      <button key={e} onClick={() => setFiltro(e)}
                        className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border text-sm font-medium transition ${filtro === e ? `${t.borde} ${t.fondo} ${t.fuerte} ring-2 ${t.anillo}` : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                        <I className="w-4 h-4" /> {d.titulo} <span className="tabular-nums font-bold">{analisis.conteo[e]}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Tabla */}
                <div className="rounded-2xl border border-gray-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                      <tr>
                        <th className="w-10 px-4 py-3">
                          {filtro === 'lista' && (
                            <input type="checkbox" className="w-4 h-4 rounded" checked={listas.length > 0 && aImportar.length === listas.length}
                              onChange={() => setMarcadas(aImportar.length === listas.length ? new Set() : new Set(listas.map(f => f.clave)))} />
                          )}
                        </th>
                        <th className="text-left px-3 py-3">Factura</th>
                        <th className="text-left px-3 py-3">Proveedor</th>
                        <th className="text-left px-3 py-3">Fecha</th>
                        <th className="text-left px-3 py-3">Condición</th>
                        <th className="text-right px-3 py-3">Total</th>
                        <th className="text-left px-3 py-3">Sus 3 archivos</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {visibles.map(f => {
                        const d = ESTADOS[f.estado]; const t = TONO[d.color];
                        return (
                          <tr key={f.clave} className={f.estado === 'lista' && marcadas.has(f.clave) ? 'bg-emerald-50/30' : ''}>
                            <td className="px-4 py-3 align-top">
                              {f.estado === 'lista' && (
                                <input type="checkbox" className="w-4 h-4 rounded mt-0.5" checked={marcadas.has(f.clave)}
                                  onChange={() => setMarcadas(prev => { const n = new Set(prev); n.has(f.clave) ? n.delete(f.clave) : n.add(f.clave); return n; })} />
                              )}
                            </td>
                            <td className="px-3 py-3 align-top whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <span className="px-1.5 py-0.5 rounded border border-indigo-300 text-indigo-700 text-[10px] font-bold font-mono">{f.tipo || '—'}</span>
                                <span className="font-semibold text-gray-900 tabular-nums">{corto(f.consecutivo)}</span>
                              </div>
                              <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${t.fondo} ${t.texto}`}>{d.corto}</span>
                            </td>
                            <td className="px-3 py-3 align-top">
                              <p className="text-gray-900 font-medium leading-snug">{f.proveedor || '—'}</p>
                              {f.problemas.length > 0 && <p className={`text-xs mt-1 ${t.texto}`}>{f.problemas.join(' · ')}</p>}
                            </td>
                            <td className="px-3 py-3 align-top text-gray-600 whitespace-nowrap">{fecha(f.fecha)}</td>
                            <td className="px-3 py-3 align-top"><Condicion cv={f.condicionVenta} plazo={f.plazoCredito} /></td>
                            <td className="px-3 py-3 align-top text-right font-semibold text-gray-900 tabular-nums whitespace-nowrap">{f.total != null ? monto(f.total, f.moneda) : '—'}</td>
                            <td className="px-3 py-3 align-top">
                              <div className="flex flex-wrap gap-1.5">
                                <Archivo ok={!!f.archivos.factura} icono={FileCode2} etiqueta="XML" nombre={f.archivos.factura} />
                                <Archivo ok={!!f.archivos.respuesta && f.estado !== 'no_aceptada'} icono={ShieldCheck} etiqueta="Hacienda" nombre={f.archivos.respuesta} detalle={f.archivos.respuestaEstado} />
                                <Archivo ok={!!f.archivos.pdf} icono={FileText} etiqueta="PDF" nombre={f.archivos.pdf ? `${f.archivos.pdf} (${f.archivos.pdfComo})` : null}
                                  onVer={() => { const conPdf = visibles.filter(x => x.archivos.pdfInterno); setVisor({ lista: conPdf, indice: conPdf.findIndex(x => x.clave === f.clave) }); }} />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* PDF sin asignar: se asignan a mano, nunca se adivinan */}
                {analisis.pdfSinAsignar.length > 0 && (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-5">
                    <p className="flex items-center gap-2 text-sm font-semibold text-amber-900"><Paperclip className="w-4 h-4" /> PDF que no se pudieron emparejar solos</p>
                    <p className="text-xs text-amber-800/80 mb-3">Su nombre no trae la clave ni el consecutivo. Si sabés de cuál factura es, asignalo.</p>
                    <div className="space-y-2">
                      {analisis.pdfSinAsignar.map(p => (
                        <div key={p.interno} className="flex flex-wrap items-center gap-3 bg-white rounded-xl border border-amber-200 px-3 py-2">
                          <FileText className="w-4 h-4 text-amber-600" />
                          <span className="text-sm text-gray-800 truncate max-w-md" title={p.archivo}>{p.archivo}</span>
                          <select defaultValue="" onChange={e => e.target.value && asignarPdf(p.interno, e.target.value)}
                            className="ml-auto text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white">
                            <option value="">Asignar a la factura…</option>
                            {analisis.facturas.filter(f => !f.archivos.pdf && f.consecutivo).map(f => (
                              <option key={f.clave} value={f.clave}>{corto(f.consecutivo)} · {f.proveedor} · {monto(f.total, f.moneda)}</option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {analisis.ignorados.length > 0 && (
                  <div className="rounded-2xl border border-gray-200">
                    <button onClick={() => setVerIgnorados(v => !v)} className="w-full flex items-center gap-2 px-5 py-3 text-sm text-gray-700">
                      {verIgnorados ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      {analisis.ignorados.length} archivo(s) ignorado(s)
                    </button>
                    {verIgnorados && (
                      <ul className="px-5 pb-4 space-y-1 text-xs text-gray-600">
                        {analisis.ignorados.map((x, i) => <li key={i}><span className="font-medium text-gray-800">{x.archivo}</span> · {x.motivo}</li>)}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {/* ───────── 3. Importando / 4. Fin ───────── */}
          {(fase === 'importando' || fase === 'fin') && (
            <div className="p-6 space-y-5">
              <div className="rounded-2xl border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-base font-semibold text-gray-900">
                    {fase === 'importando' ? `Importando ${hechas} de ${aImportar.length}…` : `Listo: ${importadas.length} de ${aImportar.length} importadas a Por confirmar`}
                  </p>
                  {fase === 'fin' && fallidas.length > 0 && <span className="text-sm text-red-700 font-medium">{fallidas.length} con error</span>}
                </div>
                <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                  <div className="h-full bg-emerald-500 transition-all" style={{ width: `${aImportar.length ? (hechas / aImportar.length) * 100 : 0}%` }} />
                </div>
                {fase === 'fin' && importadas.length > 0 && (
                  <p className="text-sm text-gray-600 mt-3">
                    Ya están en <b>Por confirmar</b>. Podés confirmarlas ante Hacienda ahora mismo, todas juntas, sin salir de aquí.
                  </p>
                )}
              </div>
              <div className="rounded-2xl border border-gray-200 divide-y divide-gray-100">
                {aImportar.map(f => {
                  const a = avance[f.clave] || {};
                  return (
                    <div key={f.clave} className="flex items-center gap-4 px-5 py-3">
                      <span className="w-6 flex justify-center">
                        {a.estado === 'subiendo' && <Loader2 className="w-5 h-5 text-indigo-600 animate-spin" />}
                        {a.estado === 'ok' && <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
                        {a.estado === 'error' && <XCircle className="w-5 h-5 text-red-600" />}
                        {(!a.estado || a.estado === 'cola') && <span className="w-2.5 h-2.5 rounded-full bg-gray-300" />}
                      </span>
                      <span className="px-1.5 py-0.5 rounded border border-indigo-300 text-indigo-700 text-[10px] font-bold font-mono">{f.tipo}</span>
                      <span className="font-semibold text-gray-900 tabular-nums w-24">{corto(f.consecutivo)}</span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm text-gray-800 truncate">{f.proveedor}</span>
                        {a.estado === 'error' && <span className="block text-xs text-red-600">{a.mensaje}</span>}
                      </span>
                      <Condicion cv={f.condicionVenta} plazo={f.plazoCredito} />
                      <span className="text-sm font-semibold text-gray-900 tabular-nums whitespace-nowrap w-32 text-right">{monto(f.total, f.moneda)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Pie con las acciones de cada fase */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="text-xs text-gray-500">
            {fase === 'resultado' && 'Las facturas con problemas no se importan. Podés agregar los archivos que falten y volver a analizar.'}
          </div>
          <div className="flex flex-wrap gap-2">
            {fase === 'seleccion' && (
              <>
                {analisis && <button onClick={() => setFase('resultado')} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 text-sm">Volver al análisis</button>}
                <button onClick={cerrar} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 text-sm">Cancelar</button>
                <button onClick={analizar} disabled={!archivos.length}
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-lg border border-indigo-500 text-indigo-700 hover:bg-indigo-50 text-sm font-semibold disabled:opacity-40">
                  <ShieldCheck className="w-4 h-4" /> Analizar {archivos.length ? `${archivos.length} archivos` : ''}
                </button>
              </>
            )}
            {fase === 'resultado' && (
              <>
                <button onClick={() => setFase('seleccion')} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 text-sm">Agregar archivos que falten</button>
                <button onClick={cerrar} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 text-sm">Cancelar</button>
                <button onClick={() => setConfirmarImportar(true)} disabled={!aImportar.length}
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-lg border border-emerald-500 text-emerald-700 hover:bg-emerald-50 text-sm font-semibold disabled:opacity-40">
                  <Inbox className="w-4 h-4" /> Importar {aImportar.length} a Por confirmar
                </button>
              </>
            )}
            {fase === 'fin' && (
              <>
                <button onClick={cerrar} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 text-sm">Cerrar</button>
                {importadas.length > 0 && ConfirmarTodas && (
                  <button onClick={() => setConfirmando(true)}
                    className="inline-flex items-center gap-2 px-5 py-2 rounded-lg border border-emerald-500 text-emerald-700 hover:bg-emerald-50 text-sm font-semibold">
                    <CheckCircle2 className="w-4 h-4" /> Confirmar las {importadas.length} ante Hacienda
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {visor && (
        <VisorPdf loteId={loteId} lista={visor.lista} indice={visor.indice}
          setIndice={(i) => setVisor(v => ({ ...v, indice: i }))} onCerrar={() => setVisor(null)} />
      )}

      <ConfirmModal
        isOpen={confirmarImportar}
        onClose={() => setConfirmarImportar(false)}
        onConfirm={importar}
        title={`Importar ${aImportar.length} factura(s)`}
        message={`Se registran en Por confirmar de ${empresa?.nombre || 'la empresa activa'}, cada una con su XML, su respuesta de Hacienda y su PDF. Todavía no se envía nada a Hacienda.`}
        confirmText="Importar"
        variant="info"
      />

      {confirmando && ConfirmarTodas && (
        <ConfirmarTodas
          count={importadas.length}
          facturaIds={importadas.map(f => avance[f.clave].facturaId)}
          facturas={importadas.map(f => ({
            id: avance[f.clave].facturaId, proveedorNombre: f.proveedor, numeroConsecutivo: f.consecutivo,
            condicionVenta: f.condicionVenta, plazoCredito: f.plazoCredito, totalComprobante: f.total, moneda: f.moneda
          }))}
          onClose={() => setConfirmando(false)}
          onSuccess={() => { setConfirmando(false); if (loteId) gastosApi.loteBorrar(loteId).catch(() => {}); onSuccess(); }}
        />
      )}
    </div>
  );
}
